/**
 * ActiveWorkersRail — sticky chip strip listing only the workers with a
 * running tool call right now. Sits above the ActivityLog inside the
 * Activity hero, vanishes when nothing is running. Click a chip to scroll
 * the matching lane into view; the lane is already auto-expanded by the
 * lane-level auto-expand logic in WorkerLanes.
 *
 * The rail derives its data from the same `activityLog` events that feed
 * WorkerLanes — pure projection, no extra wire frames.
 */
import { useTranslation } from "react-i18next"

import type { DeepActivityEvent } from "@/stores/ai-explorer-store"
import type { TFunction } from "i18next"

import { Brain, Loader2, ShieldCheck, Telescope, Wrench } from "@/components/icons"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import { computeElapsedSeconds, formatElapsedBadge } from "./activity-log-helpers"
import { buildLanes, laneStatus, type PairedTool } from "./worker-lanes-utils"

interface ActiveWorkersRailProps {
  events: DeepActivityEvent[]
}

function laneIdentity(source: string): {
  Icon: typeof Brain
  pillClass: string
} {
  switch (source) {
    case "deep_explorer":
      return {
        Icon: Brain,
        pillClass: "border-foreground/20 bg-foreground/5 text-foreground/90",
      }
    case "scout":
      return {
        Icon: Telescope,
        pillClass: CATEGORY_TONES[0],
      }
    case "fact_checker":
      return {
        Icon: ShieldCheck,
        pillClass: CATEGORY_TONES[4],
      }
    case "worker":
    default:
      return {
        Icon: Wrench,
        pillClass: CATEGORY_TONES[1],
      }
  }
}

function laneRoleLabel(source: string, t: TFunction): string {
  switch (source) {
    case "deep_explorer":
      return t("deepExplorer.main_agent")
    case "scout":
      return t("deepExplorer.role_scout", { defaultValue: "Scout" })
    case "fact_checker":
      return t("deepExplorer.role_fact_checker", { defaultValue: "Fact-checker" })
    case "worker":
      return t("deepExplorer.role_worker", { defaultValue: "Worker" })
    default:
      return source
  }
}

function pairLaneEvents(events: DeepActivityEvent[]): PairedTool[] {
  const byId = new Map<string, PairedTool>()
  const order: string[] = []
  for (const e of events) {
    let entry = byId.get(e.id)
    if (!entry) {
      entry = { id: e.id, tool: e.tool, source: e.source, start: null, end: null }
      byId.set(e.id, entry)
      order.push(e.id)
    }
    if (e.event === "tool_start") entry.start = e
    else entry.end = e
  }
  return order.map((id) => byId.get(id)!).filter(Boolean)
}

function laneRunningElapsedSeconds(paired: PairedTool[]): number {
  // Elapsed for the running lane: time since the earliest in-flight
  // (no end) tool call started. Falls back to lane's full elapsed if
  // we somehow can't find a start timestamp.
  let earliest = Number.POSITIVE_INFINITY
  for (const p of paired) {
    if (p.end) continue
    const s = p.start?.timestamp ? Date.parse(p.start.timestamp) : NaN
    if (Number.isFinite(s) && s < earliest) earliest = s
  }
  if (!Number.isFinite(earliest)) return 0
  return Math.max(0, Math.round((Date.now() - earliest) / 1000))
}

export function ActiveWorkersRail({ events }: ActiveWorkersRailProps) {
  const { t } = useTranslation()
  const lanes = buildLanes(events)
  const active = lanes
    .map((lane) => {
      const paired = pairLaneEvents(lane.events)
      const status = laneStatus(paired)
      return { lane, paired, running: status.running > 0 }
    })
    .filter((entry) => entry.running)

  if (active.length === 0) return null

  return (
    <div
      aria-label={t("deepExplorer.active_workers_aria", { defaultValue: "Active workers" })}
      className="border-border bg-muted/30 flex shrink-0 flex-wrap items-center gap-1.5 border-b px-3 py-1.5"
      role="region"
    >
      <span className="text-muted-foreground/80 mr-1 text-[10px] font-medium tracking-wide uppercase">
        {t("deepExplorer.active_now", { defaultValue: "Active now" })}
      </span>
      {active.map(({ lane, paired }) => {
        const identity = laneIdentity(lane.source)
        const RoleIcon = identity.Icon
        const roleLabel = laneRoleLabel(lane.source, t)
        const dispatchSuffix =
          lane.source !== "deep_explorer" && lane.dispatchId
            ? `#${lane.dispatchId.slice(0, 6)}`
            : null
        const elapsed = formatElapsedBadge(
          laneRunningElapsedSeconds(paired) ||
            computeElapsedSeconds(paired[0]?.start ?? null, paired[0]?.end ?? null)
        )
        return (
          <button
            key={lane.key}
            type="button"
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors hover:brightness-110",
              identity.pillClass
            )}
            onClick={() => {
              const el = document.getElementById(`worker-lane-${lane.key}`)
              el?.scrollIntoView({ behavior: "smooth", block: "nearest" })
            }}
          >
            <Loader2 className="size-3 shrink-0 animate-spin motion-reduce:animate-none" />
            <RoleIcon className="size-3 shrink-0" />
            <span>{roleLabel}</span>
            {dispatchSuffix && (
              <span className="font-mono tabular-nums opacity-80">{dispatchSuffix}</span>
            )}
            {elapsed && <span className="tabular-nums opacity-80">· {elapsed}</span>}
          </button>
        )
      })}
    </div>
  )
}
